import { twMerge } from "tailwind-merge";
import type { ReactNode } from "react";
import { Sparkles, User } from "lucide-react";
import type { ChatPersona } from "../ChatContext";

/**
 * MessageLockup — the inline message header treatment: an avatar tile +
 * the speaker's name as a header row, with the message body flush-left
 * beneath it (NOT indented under the name). Body text lines up with the
 * tool-activity cards that render as siblings, so a turn reads as one
 * left-aligned column instead of the body sitting nudged-in from its
 * own tool calls. No chat bubbles; the transcript reads top-to-bottom.
 *
 *   [🚀] Houston
 *   Your landing page looks great…
 *   [ Read 3 · Edited 2 · details ]
 */
export interface MessageLockupProps {
  persona: ChatPersona;
  tone: "assistant" | "user";
  children: ReactNode;
  /**
   * A follow-on message from the same speaker in the same turn.
   * Renders the body in the same column but with an empty gutter and no
   * name — consecutive messages read as one conversational pass instead
   * of restarting the "Houston" header for every beat.
   */
  continuation?: boolean;
  className?: string;
  testId?: string;
}

export function MessageLockup({
  persona,
  tone,
  children,
  continuation = false,
  className,
  testId,
}: MessageLockupProps) {
  return (
    <div
      data-testid={testId}
      data-role={tone}
      data-continuation={continuation || undefined}
      className={twMerge("space-y-1", className)}
    >
      {!continuation && (
        <div className="flex items-center gap-2">
          <span
            className={twMerge(
              "flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg [&>svg]:size-4",
              tone === "assistant"
                ? "bg-primary-500 text-white"
                : "border border-neutral-200 bg-white text-neutral-500",
              persona.tileClassName,
            )}
          >
            {persona.avatar ?? (tone === "assistant" ? <Sparkles /> : <User />)}
          </span>
          <div className="text-sm font-semibold leading-5 text-neutral-900">
            {persona.name}
          </div>
        </div>
      )}
      <div className="min-w-0 space-y-3">{children}</div>
    </div>
  );
}
